import { ArrowRight, ArrowUpRight, HeartPulse, Home as HomeIcon, Building2, PiggyBank, Siren } from 'lucide-react';
import { Hero } from '@/components/Hero';
import { Marquee } from '@/components/Marquee';
import { Chapters } from '@/components/Chapters';
import { FaqAccordion } from '@/components/FaqAccordion';
import { CtaBanner } from '@/components/CtaBanner';
import { TaxSavingsCalculator } from '@/components/TaxSavingsCalculator';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/components/Seo';
import { AREAS, HOME_FAQ, GUIDE_IMAGES } from '@/data/site';
import { AREAS_EN, HOME_FAQ_EN } from '@/data/en/site';
import { STATIC_IT } from '@/data/staticIt';
import { STATIC_EN } from '@/data/staticEn';
import { useLang, LangLink } from '@/i18n/LangContext';
import { SALUTE_PAGES } from '@/data/salute';
import { PRIVATI_PAGES } from '@/data/privati';
import { AZIENDE_PAGES } from '@/data/aziende';
import { SALUTE_PAGES_EN } from '@/data/en/salute';
import { PRIVATI_PAGES_EN } from '@/data/en/privati';
import { AZIENDE_PAGES_EN } from '@/data/en/aziende';

const ICONS = { HeartPulse, Home: HomeIcon, Building2, PiggyBank };


const EditorialCopy = ({ content, tone = 'light' }) => {
  if (!content) return null;
  const dark = tone === 'dark';
  return (
    <section className={dark ? 'bg-brand-deep text-white' : 'bg-background'}>
      <div className="mx-auto max-w-site px-5 md:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <p className={`text-xs uppercase tracking-[0.25em] font-semibold mb-4 ${dark ? 'text-brand-light' : 'text-primary'}`}>{content.eyebrow}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight text-balance">{content.title}</h2>
        </Reveal>
        <Reveal delay={0.08} className="lg:col-span-7 max-w-3xl">
          <div className={`space-y-5 text-base md:text-lg leading-relaxed ${dark ? 'text-white/75' : 'text-foreground/72'}`}>
            {content.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FEATURED_PATHS = [
  '/guide/privati/trasferirsi-in-svizzera',
  '/guide/privati/andare-a-vivere-da-soli',
  '/guide/privati/comprare-casa-a-30-anni',
  '/guide/privati/mettersi-in-proprio',
  '/guide/aziende/aprire-un-azienda',
  '/guide/salute/assicurazione-prenatale',
];

export default function Home() {
  const { lang, t } = useLang();
  const c = lang === 'en' ? STATIC_EN.home : STATIC_IT.home;
  const areas = lang === 'en' ? AREAS_EN : AREAS;
  const faq = lang === 'en' ? HOME_FAQ_EN : HOME_FAQ;
  const allPages = lang === 'en'
    ? [...SALUTE_PAGES_EN, ...PRIVATI_PAGES_EN, ...AZIENDE_PAGES_EN]
    : [...SALUTE_PAGES, ...PRIVATI_PAGES, ...AZIENDE_PAGES];
  const featuredGuides = FEATURED_PATHS.map((p) => allPages.find((page) => page.path === p)).filter(Boolean);
  useSeo({ ...c.seo, faq });

  return (
    <div data-testid="home-page">
      <Hero hero={c.hero} />
      <Marquee items={c.marquee} />

      <EditorialCopy content={c.introContent} />

      <section data-testid="paths-section" className="py-20 md:py-28">
        <div className="mx-auto max-w-site px-5 md:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-4">{c.paths.eyebrow}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              {c.paths.title}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
            {areas.map((area, i) => {
              const Icon = ICONS[area.icon];
              return (
                <Reveal key={area.key} delay={i * 0.08}>
                  <LangLink
                    to={area.path}
                    data-testid={`path-card-${area.key}`}
                    className="group relative block bg-white h-full overflow-hidden"
                  >
                    <div className="relative h-56 md:h-64 overflow-hidden">
                      <img
                        src={area.image}
                        alt={area.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/70 via-brand-deep/10 to-transparent" />
                      <span className="absolute top-5 left-6 font-heading text-sm font-bold text-white/90 tracking-widest">{String(i + 1).padStart(2, '0')}</span>
                      <div className="absolute bottom-5 left-6 flex items-center gap-3">
                        <span className="bg-white/95 p-2.5 rounded-lg">
                          <Icon className="h-5 w-5 text-primary" />
                        </span>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-white tracking-tight">{area.name}</h3>
                      </div>
                    </div>
                    <div className="p-6 md:p-8 border-t border-border group-hover:border-primary transition-colors duration-300">
                      <p className="text-sm md:text-base leading-relaxed text-foreground/70 mb-5">{area.short}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        {t('exploreArea')}
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1.5" />
                      </span>
                    </div>
                  </LangLink>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>


      <section data-testid="home-tax-savings" className="py-20 md:py-28 bg-white border-y border-border">
        <div className="mx-auto max-w-site px-5 md:px-8">
          <Reveal className="max-w-3xl mb-10">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-4">Pilastro 3a e imposte</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight text-balance">
              Prima vedi il risparmio, poi decidi come costruire la previdenza.
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground/70">
              Il vantaggio fiscale è spesso il primo motivo per iniziare un 3a. Usalo come punto di partenza, non come unica ragione: contano anche orizzonte, costi, flessibilità e protezione.
            </p>
          </Reveal>
          <TaxSavingsCalculator compact source="home" />
        </div>
      </section>

      <EditorialCopy content={c.reviewContent} tone="dark" />

      <section data-testid="guides-excerpt" className="py-20 md:py-28 bg-white border-y border-border">
        <div className="mx-auto max-w-site px-5 md:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-4">Guide per i momenti importanti</p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight">
                Contenuti utili, prima ancora di conoscerci.
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {featuredGuides.map((guide, i) => (
              <Reveal key={guide.path} delay={i * 0.06}>
                <LangLink
                  to={guide.path}
                  data-testid={`guide-card-${guide.path.replace(/\//g, '-')}`}
                  className="group block bg-white h-full hover:bg-brand-pale/40 transition-colors duration-300 overflow-hidden"
                >
                  {GUIDE_IMAGES[guide.path] && (
                    <div className="aspect-[16/9] overflow-hidden bg-brand-pale">
                      <img
                        src={GUIDE_IMAGES[guide.path]}
                        alt={guide.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-light mb-4">{guide.eyebrow}</p>
                  <h3 className="font-heading text-xl font-bold tracking-tight mb-3 text-balance group-hover:text-primary transition-colors duration-200">
                    {guide.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground/65 mb-6">{guide.summary}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    {t('readGuide')}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                  </div>
                </LangLink>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Chapters items={c.method.items} eyebrow={c.method.eyebrow} title={c.method.title} />

      <section data-testid="claims-block" className="bg-brand-deep text-white">
        <div className="mx-auto max-w-site px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <Reveal className="md:col-span-2">
            <span className="inline-flex bg-brand-light/15 border border-brand-light/30 p-4 rounded-lg">
              <Siren className="h-8 w-8 text-brand-light" />
            </span>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-4">
              {c.claims.title}
            </h2>
            <p className="text-white/75 text-base md:text-lg leading-relaxed">
              {c.claims.text}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="md:col-span-3 md:text-right">
            <LangLink
              to="/centro-sinistri"
              data-testid="home-claims-cta"
              className="inline-flex items-center gap-2 bg-brand-light text-brand-deep px-7 py-4 text-sm font-bold rounded-lg hover:bg-white transition-colors duration-200 group"
            >
              {c.claims.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </LangLink>
          </Reveal>
        </div>
      </section>

      <section data-testid="why-broker" className="py-20 md:py-28">
        <div className="mx-auto max-w-site px-5 md:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-4">{c.why.eyebrow}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              {c.why.title}
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {c.why.items.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="bg-white p-6 md:p-8 h-full hover:bg-brand-pale/40 transition-colors duration-300">
                  <span className="font-heading text-sm font-bold text-brand-light">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="font-heading text-lg md:text-xl font-bold tracking-tight mt-2 mb-3">{item.title}</h3>
                  <p className="text-sm md:text-base leading-relaxed text-foreground/70">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-site px-5 md:px-8 pb-20">
        <FaqAccordion items={faq} eyebrow={c.faqEyebrow} title={c.faqTitle} />
      </div>

      <div className="mx-auto max-w-site px-5 md:px-8">
        <CtaBanner
          title={c.finalCta.title}
          text={c.finalCta.text}
          label={c.finalCta.label}
        />
      </div>
    </div>
  );
}
